import { SSOCrypto } from '../types';
import { LoggerNames, PKCE } from '../lib/constants';
import { base64URLEncode, generateRandomString, sha256 } from '../lib/crypto';
import { AuthorizationError } from '../lib/authorization-error';
import { Logger } from '../lib/logger';
import { SSO_TRANSACTION_KEYS } from '../storage-manager/storage.const';
import { SimpleStorage } from '../storage-manager/storage.types';

type PkceConfig = {
  crypto: SSOCrypto;
  transactionStorage: SimpleStorage;
};

export class PkceManager {
  private readonly transactionStorage: SimpleStorage;
  private readonly crypto: SSOCrypto;
  private readonly logger: Logger = Logger.createLogger(LoggerNames.PKCE_MANAGER);

  public readonly CODE_METHOD = 'S256';

  constructor({ crypto, transactionStorage }: PkceConfig) {
    this.crypto = crypto;
    this.transactionStorage = transactionStorage;
  }

  /**
   * Creates a new pkce code verifier/challenge pair
   */
  public async createCodePair(length: number = PKCE.DEFAULT_PKCE_LENGTH) {
    this.logger.debug('Creating new PKCE code pair');
    const codeVerifier = this.createCodeVerifier(length);
    const codeChallenge = await this.createCodeChallenge(codeVerifier);
    return { codeVerifier, codeChallenge };
  }

  /**
   * Retrieves a code verifier from storage. After retrieval,
   * the code verifier will be removed from storage to prevent stale codes from persisting.
   * @throws {AuthorizationError} if no code verifier is found in storage
   */
  public retrieveCodeVerifier() {
    this.logger.debug('Retrieving PKCE code verifier from storage');
    const pkce = this.transactionStorage.getItem(SSO_TRANSACTION_KEYS.PKCE);
    if (!pkce) {
      this.logger.error('No PKCE code verifier found in storage. Cannot complete authorization flow');
      throw new AuthorizationError('MissingCodeVerifier', `No code verifier found in storage`);
    }
    return pkce;
  }

  public removeCodeVerifier() {
    this.logger.debug('Removing PKCE code verifier from storage');
    this.transactionStorage.removeItem(SSO_TRANSACTION_KEYS.PKCE);
  }

  /**
   * Creates a new pkce code verifier. The code verifier is persisted to storage.
   * This function is not pure. Each call will produce a new code verifier. Each new code verifier
   * will overwrite any previous values in storage.
   *
   * Optionally provide a PKCE length. This length will be validated and adjusted to conform to the
   * oauth spec.
   *
   * https://datatracker.ietf.org/doc/html/rfc7636#section-4.1
   * @param length
   */
  public createCodeVerifier(length: number = PKCE.DEFAULT_PKCE_LENGTH) {
    const validLength = length < PKCE.MIN_PKCE_LENGTH ? PKCE.MIN_PKCE_LENGTH : length;
    const codeVerifier = encodeURIComponent(generateRandomString(validLength, this.crypto)).slice(
      0,
      PKCE.MAX_PKCE_LENGTH,
    );
    this.logger.debug(`Created a new PKCE code verifier. Saving it to storage: ${codeVerifier}`);
    this.transactionStorage.setItem(SSO_TRANSACTION_KEYS.PKCE, codeVerifier);
    return codeVerifier;
  }

  /**
   * Creates a new pkce code challenge from the provided code verifier
   * @param codeVerifier
   */
  public async createCodeChallenge(codeVerifier: string) {
    const hash = await sha256(codeVerifier, this.crypto);
    const challenge = base64URLEncode(hash);
    this.logger.debug(`Created a new PKCE code challenge from code verifier: ${codeVerifier}`);
    return challenge;
  }
}
